/**
 * Fleet (B2B parking) — NOT a DealType, an overlay on Monthly + Parking.
 *
 * The `DealType` GraphQL enum has three values (Monthly / Broker /
 * Traditional). Fleet is NOT one of them. A multi-unit parking listing
 * with availability > FLEET_TRIGGER_AVAILABILITY_THRESHOLD still resolves
 * to the Monthly variant — keeps the Monthly "Reservar" primary CTA,
 * keeps the Monthly checkout flow, keeps the per-spot price math.
 *
 * What this file actually IS: the LeadForm config the fleet pill opens
 * when clicked. `PriceCard` (on a Monthly + Parking listing meeting the
 * availability threshold) renders an ADDITIONAL pill alongside the
 * Monthly CTA; clicking that pill calls `<LeadForm variantOverride={
 * fleetVariantOverride}>`, which threads `baseFleetVariant` in as a
 * one-off override. The underlying ListingVariant the page is rendered
 * with stays Monthly throughout.
 *
 * This file is NOT in `resolveListingVariant.ts`'s `BASE_VARIANT_BY_DEAL_TYPE`
 * map — only `monthly.ts`, `broker.ts`, `traditional.ts` are. Adding a
 * new pill-driven LeadForm overlay on top of an existing DealType =
 * add another file like this one. Adding a new DealType = update both
 * the variant map AND the GraphQL enum.
 *
 * Full rule: see spotme-client/CLAUDE.md → "Fleet — NOT a deal type".
 */

import type { BaseListingVariant } from 'src/lib/listingVariants/types'

export const baseFleetVariant: BaseListingVariant = {
  priceCard: {
    primaryCta: {
      labelKey: 'CTA.FLEET_PLAN',
      action: 'open-lead-form'
    },
    secondaryCta: null,
    // Same as Broker — recent-inquiry-driven scarcity chip was removed.
    showScarcity: false,
    scarcityCopyKey: null,
    showWatching: true,
    showStorageTimeChips: false,
    showQuantityInput: false,
    showFleetCta: false,
    showPriceBreakdown: false,
    trustSignals: [
      'broker.no_commitment',
      'broker.specialist',
      'broker.custom_terms'
    ]
  },
  leadForm: {
    enabled: true,
    desktopContainer: 'inline-on-card',
    // Aligned with Broker + Traditional: WA is the primary channel on all
    // three off-platform funnels. Fleet renters (DiDi, Rappi, last-mile)
    // expect WA-first contact the same way commercial-warehouse renters do.
    primaryChannel: 'whatsapp',
    // Step order INVERTED 2026-05-18 alongside Broker + Traditional. See
    // spotme-client/src/lib/listingVariants/variants/broker.ts for the
    // rationale: contact first so partial completers pre-create a deal,
    // qualifier second as optional enrichment. Step NAME 'qualifier' is
    // preserved to keep the type and routing surface unchanged.
    steps: ['contact', 'qualifier', 'success'],
    fields: [
      { name: 'name', required: true },
      { name: 'phone', required: true },
      // Email is REQUIRED to raise lead quality (standardized across
      // Broker + Traditional + Fleet).
      { name: 'email', required: true },
      { name: 'quantity', required: true }
    ],
    qualifierField: 'quantity',
    specialistKey: 'fernando',
    responseBadge: { show: true },
    whatsappRecipient: 'specialist',
    method: 'business_fleet',
    leadType: 'demand',
    subType: 'broker_contact',
    postSubmit: 'create-account-prompt',
    // Same as Broker: a specialist scopes the fleet deal in person.
    leadCopyKey: 'default'
  },
  sections: {
    // ⚠️ NEVER RENDERED ON A LISTING PAGE. `resolveListingVariant` maps only
    // the three DealTypes, so a fleet-grade parking listing resolves to
    // MONTHLY (anonymous host) or TRADITIONAL (hidden) and shows the fleet
    // pill on top — `baseFleetVariant` is consumed by the LeadForm alone. Kept
    // for shape-completeness; do not cite it as "what Fleet listings show".
    user: { variant: 'specialist' },
    size: { show: true, framing: 'b2b' },
    insurance: { show: false, mode: 'hidden' },
    reviews: { show: true },
    refundBadge: { show: false },
    similarListings: { show: true, tier: 'tier-up' },
    securityAndSupport: {
      // Fleet shares Broker's framing — same SpotMe Pro audit + same
      // specialist support. The H2 accent stays Broker's "acompañada"
      // so a logistics manager hopping between a warehouse and a fleet
      // listing sees consistent vocabulary.
      headlineKey: 'SECURITY.BROKER.TITLE_PREFIX',
      accentKey: 'SECURITY.BROKER.TITLE_ACCENT',
      subtitleKey: 'SECURITY.BROKER.SUBTITLE',
      cardKeys: [
        'verification_b2b',
        'anti_fraud',
        'custom_terms_b2b',
        'support_specialist_b2b'
      ]
    }
  },
  checkout: {
    authGate: 'none',
    guestEmailOk: false
  }
}
